import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, FileText, Grid3X3, List, Tag, ChevronUp, ChevronDown, BarChart3 } from 'lucide-react';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useSettings } from '@/contexts/SettingsContext';
import { calculateAge, getAgeDetails } from '@/utils/ageCalculator';
import { SheepFormDialog } from '@/components/dialogs/SheepFormDialog';
import { HealthDialog } from '@/components/dialogs/HealthDialog';
import { SheepTableView, SheepGridView } from '@/components/troupeau/SheepListView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Troupeau = ({ user }) => {
  const { isMobile } = useScreenSize();
  const { settings } = useSettings();
  const itemsPerPage = useMemo(() => settings.items_per_page || 12, [settings.items_per_page]);
  const [sheep, setSheep] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [filterSexe, setFilterSexe] = useState('tous');
  const [filterRace, setFilterRace] = useState('tous');
  const [sortBy, setSortBy] = useState('numero');
  const [viewMode, setViewMode] = useState('table');
  const [racesVisible, setRacesVisible] = useState(false);
  const [chartsVisible, setChartsVisible] = useState(false);
  const [chartType, setChartType] = useState('bars');
  const [currentPage, setCurrentPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [breedDialogOpen, setBreedDialogOpen] = useState(false);
  const [healthDialogOpen, setHealthDialogOpen] = useState(false);
  const [selectedSheep, setSelectedSheep] = useState(null);
  const [healthRecords, setHealthRecords] = useState([]);
  const [formData, setFormData] = useState({ numero: '', nom: '', date_naissance: '', race: '', sexe: 'femelle', poids: '', statut: 'actif', notes: '', mere_id: '', pere_id: '', origine: '' });
  const [breedFormData, setBreedFormData] = useState({ nom: '', description: '', couleur: '#10B981' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [healthFormData, setHealthFormData] = useState({ type: 'vaccin', nom: '', date_administration: '', prochaine_date: '', veterinaire: '', notes: '' });
  const [autoNumber, setAutoNumber] = useState(true);

  const canWrite = user?.permissions?.includes('write') || user?.role === 'admin';
  const canDelete = user?.permissions?.includes('delete') || user?.role === 'admin';

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (isMobile && viewMode === 'table') setViewMode('grid'); }, [isMobile, viewMode]);
  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterStatut, filterSexe, filterRace, sortBy]);

  const filteredSheep = useMemo(() => {
    let filtered = sheep.filter(s =>
      s.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.nom && s.nom.toLowerCase().includes(searchTerm.toLowerCase())) ||
      s.race.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filterStatut !== 'tous') filtered = filtered.filter(s => s.statut === filterStatut);
    if (filterSexe !== 'tous') filtered = filtered.filter(s => s.sexe === filterSexe);
    if (filterRace !== 'tous') filtered = filtered.filter(s => s.race === filterRace);
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'age': return new Date(a.date_naissance) - new Date(b.date_naissance);
        case 'age-desc': return new Date(b.date_naissance) - new Date(a.date_naissance);
        case 'sexe': return a.sexe.localeCompare(b.sexe);
        case 'race': return a.race.localeCompare(b.race);
        case 'race-desc': return b.race.localeCompare(a.race);
        default: return a.numero.localeCompare(b.numero, undefined, { numeric: true });
      }
    });
    return filtered;
  }, [searchTerm, sheep, filterStatut, filterSexe, filterRace, sortBy]);

  const totalPages = Math.ceil(filteredSheep.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSheep = filteredSheep.slice(startIndex, startIndex + itemsPerPage);

  const sheepByBreed = breeds.map(breed => {
    const breedSheep = sheep.filter(s => s.race === breed.nom);
    return { name: breed.nom, value: breedSheep.length, activeCount: breedSheep.filter(s => s.statut === 'actif').length, soldCount: breedSheep.filter(s => s.statut === 'vendu').length, color: breed.couleur };
  }).filter(b => b.value > 0).sort((a, b) => b.value - a.value);

  const sheepByStatus = [
    { name: 'Actifs', value: sheep.filter(s => s.statut === 'actif').length, color: '#10B981' },
    { name: 'Réservés', value: sheep.filter(s => s.statut === 'reserve').length, color: '#F59E0B' },
    { name: 'Vendus', value: sheep.filter(s => s.statut === 'vendu').length, color: '#3B82F6' },
    { name: 'Morts', value: sheep.filter(s => s.statut === 'mort').length, color: '#6B7280' }
  ].filter(s => s.value > 0);

  const fetchData = useCallback(async () => {
    try {
      const [sheepRes, breedsRes] = await Promise.all([axios.get(`${API}/sheep`), axios.get(`${API}/breeds`)]);
      setSheep(sheepRes.data); setBreeds(breedsRes.data);
    } catch { toast.error('Erreur lors du chargement des données'); }
    finally { setLoading(false); }
  }, []);

  const fetchNextNumber = async () => {
    if (!autoNumber) return;
    try { const res = await axios.get(`${API}/sheep/next-number`); setFormData(prev => ({ ...prev, numero: res.data.next_number })); }
    catch (error) { console.error('Error fetching next number:', error); }
  };

  const fetchHealthRecords = async (sheepId) => {
    try { const res = await axios.get(`${API}/health-records?sheep_id=${sheepId}`); setHealthRecords(res.data); }
    catch { toast.error('Erreur lors du chargement du carnet de santé'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canWrite) { toast.error('Permission insuffisante'); return; }
    try {
      const endpoint = selectedSheep ? `${API}/sheep/${selectedSheep.id}` : `${API}/sheep`;
      const method = selectedSheep ? 'put' : 'post';
      const dataToSend = { numero: formData.numero, date_naissance: formData.date_naissance, race: formData.race, sexe: formData.sexe, statut: formData.statut };
      if (formData.nom?.trim()) dataToSend.nom = formData.nom.trim();
      if (formData.poids) { const p = parseFloat(formData.poids); if (!isNaN(p) && p > 0) dataToSend.poids = p; }
      if (formData.notes?.trim()) dataToSend.notes = formData.notes.trim();
      if (formData.mere_id) dataToSend.mere_id = formData.mere_id;
      if (formData.pere_id) dataToSend.pere_id = formData.pere_id;
      if (formData.origine) dataToSend.origine = formData.origine;
      const response = await axios[method](endpoint, dataToSend);
      const sheepId = selectedSheep ? selectedSheep.id : response.data.id;
      if (selectedFile) {
        const photoFormData = new FormData(); photoFormData.append('file', selectedFile);
        try { await axios.post(`${API}/sheep/${sheepId}/photo`, photoFormData, { headers: { 'Content-Type': 'multipart/form-data' } }); }
        catch { toast.warning('Mouton enregistré mais erreur photo'); }
      }
      toast.success(selectedSheep ? 'Mouton modifié avec succès' : 'Mouton ajouté avec succès');
      fetchData(); setDialogOpen(false); resetForm();
    } catch (error) {
      let msg = 'Erreur lors de l\'enregistrement';
      if (error.response?.data) { const d = error.response.data; if (typeof d.detail === 'string') msg = d.detail; else if (Array.isArray(d.detail)) msg = d.detail.map(e => e.msg).join(', '); }
      toast.error(msg);
    }
  };

  const handleHealthSubmit = async (e) => {
    e.preventDefault();
    if (!canWrite) { toast.error('Permission insuffisante'); return; }
    try {
      const data = { type: healthFormData.type, nom: healthFormData.nom, date_administration: healthFormData.date_administration, sheep_id: selectedSheep.id };
      if (healthFormData.prochaine_date) data.prochaine_date = healthFormData.prochaine_date;
      if (healthFormData.notes) data.notes = healthFormData.notes;
      await axios.post(`${API}/health-records`, data);
      toast.success('Enregistrement ajouté');
      fetchHealthRecords(selectedSheep.id);
      setHealthFormData({ type: 'vaccin', nom: '', date_administration: '', prochaine_date: '', veterinaire: '', notes: '' });
    } catch { toast.error('Erreur lors de l\'enregistrement'); }
  };

  const handleDelete = async (id) => {
    if (!canDelete) { toast.error('Permission insuffisante'); return; }
    if (window.confirm('Supprimer ce mouton ?')) {
      try { await axios.delete(`${API}/sheep/${id}`); toast.success('Mouton supprimé'); fetchData(); }
      catch { toast.error('Erreur lors de la suppression'); }
    }
  };

  const handleDeleteHealthRecord = async (recordId) => {
    if (!canDelete) { toast.error('Permission insuffisante'); return; }
    try { await axios.delete(`${API}/health-records/${recordId}`); toast.success('Enregistrement supprimé'); fetchHealthRecords(selectedSheep.id); }
    catch { toast.error('Erreur lors de la suppression'); }
  };

  const openDialog = (s = null) => {
    if (s) {
      setSelectedSheep(s);
      setFormData({ numero: s.numero, nom: s.nom || '', date_naissance: s.date_naissance, race: s.race, sexe: s.sexe, poids: s.poids || '', statut: s.statut, notes: s.notes || '', mere_id: s.mere_id || '', pere_id: s.pere_id || '', origine: s.origine || '' });
      setAutoNumber(false);
    } else { setSelectedSheep(null); resetForm(); setAutoNumber(true); fetchNextNumber(); }
    setDialogOpen(true);
  };

  const openHealthDialog = (s) => { setSelectedSheep(s); fetchHealthRecords(s.id); setHealthDialogOpen(true); };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) { setSelectedFile(file); const reader = new FileReader(); reader.onloadend = () => setPhotoPreview(reader.result); reader.readAsDataURL(file); }
  };

  const handleDeletePhoto = async () => {
    if (!selectedSheep?.photo_url || !window.confirm('Supprimer la photo ?')) return;
    try { await axios.delete(`${API}/sheep/${selectedSheep.id}/photo`); toast.success('Photo supprimée'); setSelectedSheep({ ...selectedSheep, photo_url: null }); setPhotoPreview(null); fetchData(); }
    catch { toast.error('Erreur lors de la suppression'); }
  };

  const resetForm = () => {
    setFormData({ numero: '', nom: '', date_naissance: '', race: '', sexe: 'femelle', poids: '', statut: 'actif', notes: '', mere_id: '', pere_id: '', origine: '' });
    setSelectedSheep(null); setSelectedFile(null); setPhotoPreview(null);
    if (autoNumber) setTimeout(() => fetchNextNumber(), 100);
  };

  const femelles = sheep.filter(s => s.sexe === 'femelle' && s.statut === 'actif');
  const males = sheep.filter(s => s.sexe === 'male' && s.statut === 'actif');

  const handleBreedSubmit = async (e) => {
    e.preventDefault();
    if (!canWrite) { toast.error('Permission insuffisante'); return; }
    try { await axios.post(`${API}/breeds`, breedFormData); toast.success('Race ajoutée'); fetchData(); setBreedDialogOpen(false); setBreedFormData({ nom: '', description: '', couleur: '#10B981' }); }
    catch { toast.error('Erreur lors de l\'ajout'); }
  };

  const handleDeleteBreed = async (breedId) => {
    if (!canDelete) { toast.error('Permission insuffisante'); return; }
    if (window.confirm('Supprimer cette race ?')) {
      try { await axios.delete(`${API}/breeds/${breedId}`); toast.success('Race supprimée'); fetchData(); }
      catch { toast.error('Erreur lors de la suppression'); }
    }
  };

  const renderChart = () => {
    const data = chartType === 'breeds' ? sheepByBreed : sheepByStatus;
    const total = data.reduce((sum, item) => sum + item.value, 0);
    return (
      <div className="space-y-4">
        {data.map((item) => {
          const pct = total > 0 ? (item.value / total) * 100 : 0;
          return (
            <div key={item.name} className="space-y-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2"><div className="w-4 h-4 rounded-full" style={{ backgroundColor: item.color }}></div><span className="font-medium text-gray-700 dark:text-gray-300">{item.name}</span></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">{item.value} mouton(s)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-lg h-6"><div className="h-6 rounded-lg transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: item.color }} /></div>
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                {chartType === 'breeds' ? <span>{item.activeCount} actifs, {item.soldCount} vendus</span> : <span>{item.name}</span>}
                <span>{pct.toFixed(1)}%</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="spinner"></div></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="troupeau-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Gestion du Troupeau</h1>
        {canWrite && (
          <Button onClick={() => openDialog()} className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600" data-testid="add-sheep-button">
            <Plus className="w-4 h-4 mr-2" />Ajouter un mouton
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md border-l-4 border-l-blue-500"><CardContent className="pt-6"><div className="flex items-center"><Tag className="w-8 h-8 text-blue-600 dark:text-blue-400" /><div className="ml-4"><p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total</p><p className="text-2xl font-bold text-blue-600 dark:text-blue-400" data-testid="stat-total-sheep">{sheep.length}</p></div></div></CardContent></Card>
        <Card className="border-0 shadow-md border-l-4 border-l-slate-500"><CardContent className="pt-6"><div className="flex items-center"><Tag className="w-8 h-8 text-slate-700 dark:text-slate-300" /><div className="ml-4"><p className="text-sm font-medium text-gray-600 dark:text-gray-400">Actifs</p><p className="text-2xl font-bold text-slate-700 dark:text-slate-300" data-testid="stat-active-sheep">{sheep.filter(s => s.statut === 'actif').length}</p></div></div></CardContent></Card>
        <Card className="border-0 shadow-md border-l-4 border-l-sky-500"><CardContent className="pt-6"><div className="flex items-center"><Tag className="w-8 h-8 text-sky-600 dark:text-sky-400" /><div className="ml-4"><p className="text-sm font-medium text-gray-600 dark:text-gray-400">Mâles</p><p className="text-2xl font-bold text-sky-600 dark:text-sky-400" data-testid="stat-male-sheep">{sheep.filter(s => s.sexe === 'male').length}</p></div></div></CardContent></Card>
        <Card className="border-0 shadow-md border-l-4 border-l-pink-500"><CardContent className="pt-6"><div className="flex items-center"><Tag className="w-8 h-8 text-pink-600 dark:text-pink-400" /><div className="ml-4"><p className="text-sm font-medium text-gray-600 dark:text-gray-400">Femelles</p><p className="text-2xl font-bold text-pink-600 dark:text-pink-400" data-testid="stat-female-sheep">{sheep.filter(s => s.sexe === 'femelle').length}</p></div></div></CardContent></Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="space-y-2"><Label>Statut</Label><Select value={filterStatut} onValueChange={setFilterStatut}><SelectTrigger data-testid="filter-statut"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="tous">Tous</SelectItem><SelectItem value="actif">Actifs</SelectItem><SelectItem value="reserve">Réservés</SelectItem><SelectItem value="vendu">Vendus</SelectItem><SelectItem value="mort">Morts</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>Sexe</Label><Select value={filterSexe} onValueChange={setFilterSexe}><SelectTrigger data-testid="filter-sexe"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="tous">Tous</SelectItem><SelectItem value="male">Mâles</SelectItem><SelectItem value="femelle">Femelles</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>Race</Label><Select value={filterRace} onValueChange={setFilterRace}><SelectTrigger data-testid="filter-race"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="tous">Toutes</SelectItem>{breeds.map(b => <SelectItem key={b.id} value={b.nom}>{b.nom}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Trier par</Label><Select value={sortBy} onValueChange={setSortBy}><SelectTrigger data-testid="sort-by"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="numero">Numéro</SelectItem><SelectItem value="age">Âge (jeune)</SelectItem><SelectItem value="age-desc">Âge (ancien)</SelectItem><SelectItem value="sexe">Sexe</SelectItem><SelectItem value="race">Race A-Z</SelectItem><SelectItem value="race-desc">Race Z-A</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>Affichage</Label><div className="flex gap-1">{!isMobile && <Button type="button" variant={viewMode === 'table' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('table')} data-testid="table-view-button"><List className="w-4 h-4" /></Button>}<Button type="button" variant={viewMode === 'grid' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('grid')} data-testid="grid-view-button"><Grid3X3 className="w-4 h-4" /></Button></div></div>
            <div className="space-y-2"><Label>Résultats</Label><div className="flex gap-1 items-center"><div className="h-10 flex items-center px-3 bg-gray-50 dark:bg-gray-800 rounded-lg border flex-1"><span className="text-sm font-medium text-gray-700 dark:text-gray-300">{filteredSheep.length} mouton(s)</span></div>{(filterStatut !== 'tous' || filterSexe !== 'tous' || filterRace !== 'tous') && <Button variant="outline" size="sm" onClick={() => { setFilterStatut('tous'); setFilterSexe('tous'); setFilterRace('tous'); }} className="h-10 px-2" data-testid="reset-filters">✕</Button>}</div></div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setRacesVisible(!racesVisible)} className="flex-1 justify-between"><div className="flex items-center gap-2"><Tag className="w-4 h-4" />Races</div>{racesVisible ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</Button>
            <Button variant="outline" size="sm" onClick={() => setChartsVisible(!chartsVisible)} className="flex-1 justify-between"><div className="flex items-center gap-2"><BarChart3 className="w-4 h-4" />Graphiques</div>{chartsVisible ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</Button>
          </div>
        </CardContent>
      </Card>

      {/* Races Section */}
      {racesVisible && (
        <Card className="border-0 shadow-md">
          <CardHeader><div className="flex items-center justify-between"><CardTitle className="text-lg flex items-center gap-2"><Tag className="w-5 h-5 text-slate-700 dark:text-slate-300" />Races du troupeau</CardTitle>{canWrite && <Button onClick={() => setBreedDialogOpen(true)} variant="outline" size="sm" className="border-gray-200 dark:border-gray-800"><Plus className="w-4 h-4 mr-2" />Ajouter une race</Button>}</div></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {breeds.map(breed => (
                <div key={breed.id} className="p-4 border-l-4 bg-gray-50 dark:bg-gray-950 rounded-lg hover:shadow-md transition-all group" style={{ borderLeftColor: breed.couleur }} data-testid={`breed-${breed.nom}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1"><div className="font-medium text-gray-900 dark:text-white">{breed.nom}</div>{breed.description && <div className="text-xs text-gray-600 mt-1">{breed.description}</div>}<div className="text-xs text-gray-500 mt-2">{sheep.filter(s => s.race === breed.nom).length} mouton(s)</div></div>
                    {canDelete && <Button variant="ghost" size="sm" onClick={() => handleDeleteBreed(breed.id)} className="opacity-0 group-hover:opacity-100 text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      {chartsVisible && (
        <Card className="border-0 shadow-md">
          <CardHeader><div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"><CardTitle className="text-lg flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />Répartition du troupeau</CardTitle><Select value={chartType} onValueChange={setChartType}><SelectTrigger className="w-full sm:w-64"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="breeds">Par race</SelectItem><SelectItem value="status">Par statut</SelectItem></SelectContent></Select></div></CardHeader>
          <CardContent>{(sheepByBreed.length > 0 || sheepByStatus.length > 0) ? (<div className="space-y-4"><div className="text-center text-sm text-gray-600 mb-4">Total: <span className="font-semibold">{sheep.length} mouton(s)</span></div>{renderChart()}</div>) : (<div className="text-center py-12 text-gray-500"><BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p>Aucune donnée</p></div>)}</CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input placeholder="Rechercher par numéro, nom ou race..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 border-gray-200 dark:border-gray-800 focus:border-slate-500" data-testid="search-input" />
      </div>

      {/* Display */}
      {viewMode === 'grid' && <SheepGridView sheep={paginatedSheep} allSheep={sheep} breeds={breeds} BACKEND_URL={BACKEND_URL} calculateAge={calculateAge} canWrite={canWrite} canDelete={canDelete} onEdit={openDialog} onHealth={openHealthDialog} onDelete={handleDelete} currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />}
      {viewMode === 'table' && <SheepTableView sheep={paginatedSheep} allSheep={sheep} breeds={breeds} BACKEND_URL={BACKEND_URL} calculateAge={calculateAge} getAgeDetails={getAgeDetails} canWrite={canWrite} canDelete={canDelete} onEdit={openDialog} onHealth={openHealthDialog} onDelete={handleDelete} currentPage={currentPage} totalPages={totalPages} startIndex={startIndex} itemsPerPage={itemsPerPage} totalItems={filteredSheep.length} onPageChange={setCurrentPage} />}

      {/* Dialogs */}
      <SheepFormDialog open={dialogOpen} onOpenChange={setDialogOpen} selectedSheep={selectedSheep} formData={formData} setFormData={setFormData} autoNumber={autoNumber} setAutoNumber={setAutoNumber} onFetchNextNumber={fetchNextNumber} onSubmit={handleSubmit} breeds={breeds} femelles={femelles} males={males} photoPreview={photoPreview} onFileChange={handleFileChange} onDeletePhoto={handleDeletePhoto} />
      <HealthDialog open={healthDialogOpen} onOpenChange={setHealthDialogOpen} selectedSheep={selectedSheep} healthFormData={healthFormData} setHealthFormData={setHealthFormData} onSubmit={handleHealthSubmit} healthRecords={healthRecords} canWrite={canWrite} canDelete={canDelete} onDeleteRecord={handleDeleteHealthRecord} />

      {/* Breed Dialog */}
      <Dialog open={breedDialogOpen} onOpenChange={setBreedDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ajouter une race</DialogTitle><DialogDescription>Créez une nouvelle race de mouton</DialogDescription></DialogHeader>
          <form onSubmit={handleBreedSubmit} className="space-y-4">
            <div className="space-y-2"><Label htmlFor="breed-nom">Nom *</Label><Input id="breed-nom" value={breedFormData.nom} onChange={(e) => setBreedFormData({ ...breedFormData, nom: e.target.value })} required placeholder="Ex: Merinos, Lacaune..." data-testid="breed-nom-input" /></div>
            <div className="space-y-2"><Label htmlFor="breed-description">Description</Label><Textarea id="breed-description" value={breedFormData.description} onChange={(e) => setBreedFormData({ ...breedFormData, description: e.target.value })} placeholder="Optionnel" data-testid="breed-description-input" /></div>
            <div className="space-y-2"><Label htmlFor="breed-couleur">Couleur</Label><Input id="breed-couleur" type="color" value={breedFormData.couleur} onChange={(e) => setBreedFormData({ ...breedFormData, couleur: e.target.value })} data-testid="breed-couleur-input" /></div>
            <div className="flex gap-3"><Button type="submit" className="flex-1 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600" data-testid="breed-submit-button">Ajouter la race</Button><Button type="button" variant="outline" onClick={() => setBreedDialogOpen(false)}>Annuler</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Troupeau;
